import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import api from '../../services/api';
import ModalForm from '../modal-form';
import { TipoFormulario } from '../../services/constantes';
import {
    Banco,
    Campo,
    ConciliarBancoRequest,
    ConciliarBancoResponse,
    Locacao,
    Opcao,
} from '../../services/tipos';
import { obterIdTenent } from '../../services/seguranca';
import { useAppSelector } from '../../store/hooks';

interface Props {
    show: boolean;
    locacao: Locacao | null;
    onClose: () => void;
    onSuccess: () => void;
}

interface Contadores {
    totalPagamentos: number;
    pagamentosSemBanco: number;
    totalLancamentos: number;
    lancamentosSemBanco: number;
}

interface EntidadeConciliar {
    idBanco: string;
    aplicarATodos: boolean;
    apenasSemBanco: boolean;
}

export default function ModalConciliarBanco({ show, locacao, onClose, onSuccess }: Props) {
    const token = useAppSelector(state => state.token);
    const [bancosOpcoes, setBancosOpcoes] = useState<Opcao[]>([]);
    const [contadores, setContadores] = useState<Contadores | null>(null);
    const [entidadeSelecionada, setEntidadeSelecionada] = useState<EntidadeConciliar>({ idBanco: '', aplicarATodos: false, apenasSemBanco: true });
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!show || !locacao) {
            setContadores(null);
            setBancosOpcoes([]);
            setEntidadeSelecionada({ idBanco: '', aplicarATodos: false, apenasSemBanco: true });
            setSalvando(false);
            return;
        }

        api.get(`/bancoPorEmpresa/${obterIdTenent(token)}?apenasAtivos=true`)
            .then(r => setBancosOpcoes((r.data as Banco[]).map(b => ({ label: b.apelido, valor: b.id! }))))
            .catch(() => setBancosOpcoes([]));

        api.get(`/locacao/${locacao.id}/contadores-conciliacao`)
            .then(r => setContadores(r.data))
            .catch(() => setContadores(null));
    }, [show, locacao, token]);

    const mensagemContadores = useMemo(() => {
        if (!contadores) return '';
        return `${contadores.totalPagamentos} pagamentos (${contadores.pagamentosSemBanco} sem banco) • ${contadores.totalLancamentos} lançamentos (${contadores.lancamentosSemBanco} sem banco)`;
    }, [contadores]);

    const campos: Campo[] = useMemo(() => [
        {
            label: 'Banco', nome: 'idBanco', tipo: 'select', required: true,
            largura: 12, coluna: 1, linha: 1, visible: true,
            opcoes: bancosOpcoes, selectBoxIsCleareable: true,
        },
        {
            label: 'Apenas nos sem banco definido (recomendado)', nome: 'apenasSemBanco', tipo: 'checkbox', required: false,
            largura: 12, coluna: 1, linha: 2, visible: true,
            onCampoDigitar: (valor: boolean) => {
                // Mutuamente exclusivo com "aplicarATodos"; sempre exatamente uma opção ativa.
                setEntidadeSelecionada(prev => ({
                    ...prev,
                    apenasSemBanco: valor ? true : prev.apenasSemBanco,
                    aplicarATodos: valor ? false : prev.aplicarATodos,
                }));
            },
        },
        {
            label: 'Aplicar em todos (sobrescrever)', nome: 'aplicarATodos', tipo: 'checkbox', required: false,
            largura: 12, coluna: 1, linha: 3, visible: true,
            onCampoDigitar: (valor: boolean) => {
                setEntidadeSelecionada(prev => ({
                    ...prev,
                    aplicarATodos: valor ? true : prev.aplicarATodos,
                    apenasSemBanco: valor ? false : prev.apenasSemBanco,
                }));
            },
        },
        {
            label: '', nome: '_contadoresConciliacao', tipo: 'aviso', required: false,
            largura: 12, coluna: 1, linha: 4, visible: !!contadores,
            alertType: 'info',
            alertMessage: mensagemContadores,
        },
    ], [bancosOpcoes, contadores, mensagemContadores]);

    const handleSalvar = async (entidade: EntidadeConciliar) => {
        if (salvando) return; // previne double-submit
        if (!locacao || !entidade?.idBanco) {
            toast.warn('Selecione um banco.');
            return;
        }
        setSalvando(true);
        try {
            const req: ConciliarBancoRequest = {
                idBanco: entidade.idBanco,
                aplicarATodos: !!entidade.aplicarATodos,
            };
            const { data } = await api.post<ConciliarBancoResponse>(
                `/locacao/${locacao.id}/conciliar-banco`,
                req,
            );
            toast.success(
                `${data.pagamentosAtualizados} pagamento(s) e ${data.lancamentosAtualizados} lançamento(s) atualizados.`,
            );
            onSuccess();
            onClose();
        } catch (e) {
            const err = e as AxiosError<{ Mensagem?: string }>;
            toast.error(err.response?.data?.Mensagem ?? 'Erro ao conciliar banco.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <ModalForm
            show={show}
            modo={TipoFormulario.tpCadastro}
            tamanho="lg"
            titulo="Conciliar banco"
            tituloBotaoOk={salvando ? 'Conciliando...' : 'Conciliar'}
            campos={campos}
            carregando={salvando}
            onHide={onClose}
            onCancel={onClose}
            onSave={handleSalvar}
            entidadeSelecionada={entidadeSelecionada}
            apiConfig={{
                endpoint: 'locacao',
                idTenentCampo: '',
                requisicaoBemSucedida: () => { },
                tratarErro: () => { },
            }}
        />
    );
}
